import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  createCampaignApi,
  getCampaignApi,
  getFormSettingsApi,
  listCampaignStoresApi,
  searchCitiesApi,
  updateCampaignApi,
  type BudgetType,
  type CampaignDetail,
  type CampaignQuestion,
  type CampaignStore,
  type FormSettings,
  type LocationType,
  type MetaCity,
} from "../campanhasApi";
import { CAMPANHAS_PAGE, CAMPANHA_PAGE } from "../routes";
import "./Campanhas.css";

type Loc = {
  key: string;
  type: LocationType;
  storeId?: string;
  metaKey?: string;
  label: string;
  latitude?: number;
  longitude?: number;
  radiusKm: number;
};

const BUDGET_TYPES: { value: BudgetType; label: string }[] = [
  { value: "DIARIO", label: "Por dia" },
  { value: "TOTAL", label: "Total do período" },
];

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const newId = () => Math.random().toString(36).slice(2, 10);

/** Criar e editar a campanha: vaga, região, orçamento e período. Os anúncios entram na tela da campanha. */
const CampanhaFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(id ? CAMPANHA_PAGE(id) : CAMPANHAS_PAGE());

  const [settings, setSettings] = useState<FormSettings | null>(null);
  const [stores, setStores] = useState<CampaignStore[]>([]);
  const [original, setOriginal] = useState<CampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [questions, setQuestions] = useState<CampaignQuestion[]>([]);
  const [resumeRequired, setResumeRequired] = useState(false);
  const [budgetType, setBudgetType] = useState<BudgetType>("DIARIO");
  const [budget, setBudget] = useState("30");
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [locations, setLocations] = useState<Loc[]>([]);

  const [storePick, setStorePick] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [cities, setCities] = useState<MetaCity[]>([]);
  const [citySearching, setCitySearching] = useState(false);

  // A empresa nunca usa a categoria Emprego (decisão do usuário, 01/11/2026): loja e lugar aceitam raio a partir de 1 km.
  // Sem a categoria Emprego, a loja aceita raio pequeno; a cidade continua com o mínimo da Meta.
  const minRadiusFor = (type: LocationType) => (type === "CIDADE" ? (settings?.minCityRadiusKm ?? 17) : (settings?.minRadiusNoCategoryKm ?? 1));
  // Raio inicial de uma loja nova.
  const minRadius = 5;
  const published = Boolean(original && original.status !== "RASCUNHO" && original.status !== "ERRO");
  const locked = Boolean(original?.publishedAt) || published;

  useEffect(() => {
    (async () => {
      try {
        const [s, st, c] = await Promise.all([getFormSettingsApi(), listCampaignStoresApi(), id ? getCampaignApi(id) : Promise.resolve(null)]);
        setSettings(s);
        setStores(st);
        if (c) {
          if (c.status === "ENCERRADA" || c.status === "PUBLICANDO") {
            toast.error("Campanha encerrada ou em publicação não muda.");
            navigate(CAMPANHA_PAGE(c.id), { replace: true });
            return;
          }
          setOriginal(c);
          setName(c.name);
          setJobTitle(c.jobTitle);
          setJobDescription(c.jobDescription);
          setQuestions(c.questions ?? []);
          setResumeRequired(c.resumeRequired);
          setBudgetType(c.budgetType);
          setBudget(String(c.budgetCents / 100));
          setStartDate(c.startDate.slice(0, 10));
          setEndDate(c.endDate ? c.endDate.slice(0, 10) : "");
          setLocations(
            c.locations.map((l) => ({
              key: l.id,
              type: l.type,
              storeId: l.storeId ?? undefined,
              metaKey: l.metaKey ?? undefined,
              latitude: l.latitude ?? undefined,
              longitude: l.longitude ?? undefined,
              label: l.type === "LOJA" ? (l.storeName ?? l.label) : l.label,
              radiusKm: l.radiusKm,
            })),
          );
        }
      } catch (e: any) {
        toast.error(e?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Busca de cidade na Meta, depois de uma pausa na digitação.
  useEffect(() => {
    const q = cityQuery.trim();
    if (q.length < 2 || !settings?.metaEnabled) {
      setCities([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setCitySearching(true);
        setCities(await searchCitiesApi(q));
      } catch (e: any) {
        toast.error(e?.message);
      } finally {
        setCitySearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [cityQuery, settings?.metaEnabled]);

  const availableStores = useMemo(() => stores.filter((s) => !locations.some((l) => l.storeId === s.id)), [stores, locations]);

  const addStore = () => {
    const store = stores.find((s) => s.id === storePick);
    if (!store) return;
    setLocations((prev) => [...prev, { key: newId(), type: "LOJA", storeId: store.id, label: store.name, radiusKm: minRadius }]);
    setStorePick("");
  };

  const addCity = (city: MetaCity) => {
    if (locations.some((l) => l.metaKey === city.key)) return toast.info("Essa cidade já está na lista.");
    const label = city.region ? `${city.name} (${city.region})` : city.name;
    setLocations((prev) => [...prev, { key: newId(), type: "CIDADE", metaKey: city.key, label, radiusKm: minRadiusFor("CIDADE") }]);
    setCityQuery("");
    setCities([]);
  };

  const dailyEstimate = useMemo(() => {
    const value = Number(budget.replace(",", "."));
    if (!value) return null;
    if (budgetType === "DIARIO") return value;
    if (!endDate) return null;
    const days = Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000) + 1;
    return days > 0 ? value / days : null;
  }, [budget, budgetType, startDate, endDate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locations.length) return toast.error("Inclua ao menos um local (loja ou cidade).");
    if (questions.some((q) => !q.label.trim())) return toast.error("Preencha o texto de todas as perguntas.");
    const value = Number(budget.replace(",", "."));
    if (!value || value < 1) return toast.error("Informe o orçamento, a partir de R$ 1,00.");
    const payload = {
      name: name.trim(),
      jobTitle: jobTitle.trim(),
      jobDescription: jobDescription.trim(),
      questions: questions.map((q) => ({ ...q, label: q.label.trim() })),
      resumeRequired,
      employmentCategory: false,
      budgetType,
      budget: Math.round(value * 100) / 100,
      startDate,
      endDate: endDate || null,
      locations: locations.map((l) => ({
        type: l.type,
        ...(l.type === "LOJA"
          ? { storeId: l.storeId }
          : l.type === "ENDERECO"
            ? { label: l.label, latitude: l.latitude, longitude: l.longitude }
            : { metaKey: l.metaKey, label: l.label }),
        radiusKm: l.radiusKm,
      })),
    };
    try {
      setBusy(true);
      if (id) {
        await updateCampaignApi(id, payload);
        toast.success(published ? "Campanha salva e enviada à Meta." : "Campanha salva.");
        navigate(CAMPANHA_PAGE(id), { replace: true });
      } else {
        const created = await createCampaignApi(payload);
        toast.success("Rascunho criado. Agora inclua os anúncios.");
        navigate(CAMPANHA_PAGE(created.id), { replace: true });
      }
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setBusy(false);
    }
  };

  const title = id ? "Editar campanha" : "Nova campanha";

  return (
    <div className="cmpPage">
      <main className="cmpPage__main cmpPage__main--narrow">
        <EcoPageHeader
          crumbs={[
            { label: "Campanhas", onClick: () => navigate(CAMPANHAS_PAGE()) },
            ...(original ? [{ label: original.name, onClick: () => navigate(CAMPANHA_PAGE(original.id)) }] : []),
            { label: id ? "Editar" : "Nova" },
          ]}
          title={title}
          subtitle="A vaga aparece na página de candidatura. Região, orçamento e período vão para a Meta quando a campanha for publicada."
          onBack={goBack}
          backLabel="Voltar"
        />

        {loading ? (
          <p className="cmpLoading">Carregando...</p>
        ) : (
          <form className="cmpPanel" style={{ padding: 0, gap: 20 }} onSubmit={submit}>
            {settings && !settings.metaEnabled ? (
              <p className="cmpNotice">
                <EcoIcon name="alertTriangle" size={16} />
                <span>A integração com a Meta ainda não está ligada no servidor. Dá para montar o rascunho; a publicação fica liberada quando ela for configurada.</span>
              </p>
            ) : null}
            {settings && !settings.privacyReady ? (
              <p className="cmpNotice">
                <EcoIcon name="alertTriangle" size={16} />
                <span>
                  Faltam os dados da política de privacidade (razão social, CNPJ e e-mail). Sem eles a página de candidatura fica fechada e a campanha
                  não é publicada. Um administrador preenche em Administração → Configurações → Campanhas.
                </span>
              </p>
            ) : null}
            {published ? (
              <p className="cmpNotice cmpNotice--info">
                <EcoIcon name="alertTriangle" size={16} />
                <span>A campanha já está na Meta: orçamento, data de fim e locais vão para lá ao salvar. O tipo de orçamento e o início não mudam mais.</span>
              </p>
            ) : null}

            <div className="cmpFormGrid">
            <section className="ecoCard cmpPanel">
              <h2 className="cmpPanel__title">Vaga</h2>
              <div className="cmpGrid">
                <label className="ecoField">
                  <span className="ecoLabel">Nome da campanha</span>
                  <input className="ecoInput" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Entregadores Niterói — novembro" />
                  <span className="cmpHint">Só a equipe vê. Também vira o endereço da página.</span>
                </label>
                <label className="ecoField">
                  <span className="ecoLabel">Título da vaga</span>
                  <input className="ecoInput" required maxLength={120} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Ex.: Entregador freelancer" />
                  <span className="cmpHint">O candidato vê no topo da página.</span>
                </label>
              </div>
              <label className="ecoField">
                <span className="ecoLabel">Descrição da vaga</span>
                <textarea
                  className="ecoInput cmpTextarea"
                  required
                  rows={6}
                  maxLength={5000}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="O que a pessoa faz, onde, quanto recebe, requisitos..."
                />
              </label>

              <div className="ecoField">
                <span className="ecoLabel">
                  Perguntas extras <span className="ecoLabel__optional">(até 5)</span>
                </span>
                {questions.length ? (
                  <ul className="cmpList">
                    {questions.map((q, i) => (
                      <li key={q.id} className="cmpListItem cmpListItem--question">
                        <input
                          className="ecoInput"
                          aria-label={`Pergunta ${i + 1}`}
                          maxLength={200}
                          value={q.label}
                          onChange={(e) => setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, label: e.target.value } : x)))}
                          placeholder="Ex.: Você tem moto com documentação em dia?"
                        />
                        <select
                          className="ecoSelect"
                          aria-label="Tipo de resposta"
                          value={q.type}
                          onChange={(e) => setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, type: e.target.value as CampaignQuestion["type"] } : x)))}
                        >
                          <option value="SIM_NAO">Sim ou não</option>
                          <option value="TEXTO">Texto curto</option>
                        </select>
                        <label className="cmpCheck">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) => setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, required: e.target.checked } : x)))}
                          />
                          Obrigatória
                        </label>
                        <button
                          type="button"
                          className="ecoBtn ecoBtn--icon"
                          aria-label={`Tirar a pergunta ${i + 1}`}
                          onClick={() => setQuestions((prev) => prev.filter((x) => x.id !== q.id))}
                        >
                          <EcoIcon name="x" size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="cmpHint">O formulário já pede nome, CPF, celular, e-mail (opcional), cidade e bairro.</p>
                )}
                {questions.length < 5 ? (
                  <div>
                    <button
                      type="button"
                      className="ecoBtn ecoBtn--sm"
                      onClick={() => setQuestions((prev) => [...prev, { id: newId(), label: "", type: "SIM_NAO", required: true }])}
                    >
                      <EcoIcon name="plus" size={16} />
                      Incluir pergunta
                    </button>
                  </div>
                ) : null}
              </div>

              <label className="cmpCheck">
                <input type="checkbox" checked={resumeRequired} onChange={(e) => setResumeRequired(e.target.checked)} />
                Currículo obrigatório (sem marcar, o candidato pode enviar ou não)
              </label>
            </section>

            <div className="cmpFormCol">
            <section className="ecoCard cmpPanel">
              <h2 className="cmpPanel__title">Região</h2>
              <p className="cmpHint">
                O anúncio aparece para quem está a até o raio de cada local. Locais que se sobrepõem (uma loja dentro da cidade escolhida) a Meta
                recusa.
              </p>

              <p className="cmpHint">
                Em volta de uma loja ou de um lugar, o raio vai de {settings?.minRadiusNoCategoryKm ?? 1} a 80 km; para uma cidade inteira, a Meta pede no mínimo{" "}
                {settings?.minCityRadiusKm ?? 17} km.
              </p>

              {locations.length ? (
                <ul className="cmpList">
                  {locations.map((l) => (
                    <li key={l.key} className="cmpListItem">
                      <span className="cmpListItem__label">
                        <span className="cmpStrong">{l.label}</span>
                        <span className="cmpSub">{l.type === "LOJA" ? "Loja do sistema" : l.type === "ENDERECO" ? "Lugar informado (o raio sai daqui)" : "Cidade"}</span>
                      </span>
                      <label className="ecoField">
                        <span className="ecoVisuallyHidden">Raio de {l.label} em km</span>
                        <input
                          className="ecoInput ecoInput--sm"
                          type="number"
                          min={minRadiusFor(l.type)}
                          max={80}
                          step={1}
                          required
                          value={l.radiusKm}
                          onChange={(e) => setLocations((prev) => prev.map((x) => (x.key === l.key ? { ...x, radiusKm: Number(e.target.value) } : x)))}
                        />
                      </label>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--icon"
                        aria-label={`Tirar ${l.label}`}
                        onClick={() => setLocations((prev) => prev.filter((x) => x.key !== l.key))}
                      >
                        <EcoIcon name="x" size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="ecoEmpty">Nenhum local ainda.</p>
              )}

              <div className="cmpGrid">
                <div className="ecoField">
                  <span className="ecoLabel">Incluir loja</span>
                  <div className="cmpActions" style={{ flexWrap: "nowrap" }}>
                    <select className="ecoSelect" value={storePick} onChange={(e) => setStorePick(e.target.value)} aria-label="Loja">
                      <option value="">{availableStores.length ? "Escolha a loja" : "Nenhuma loja com posição cadastrada"}</option>
                      {availableStores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                          {s.state ? ` (${s.state})` : ""}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="ecoBtn" onClick={addStore} disabled={!storePick}>
                      Incluir
                    </button>
                  </div>
                  <span className="cmpHint">Só aparecem lojas com posição cadastrada na ficha da loja.</span>
                </div>
                <div className="ecoField cmpSuggest">
                  <span className="ecoLabel">Incluir cidade</span>
                  <input
                    className="ecoInput"
                    value={cityQuery}
                    onChange={(e) => setCityQuery(e.target.value)}
                    placeholder={settings?.metaEnabled ? "Digite o nome da cidade" : "Busca indisponível sem a integração"}
                    disabled={!settings?.metaEnabled}
                    aria-autocomplete="list"
                  />
                  {citySearching ? <span className="cmpHint">Buscando...</span> : null}
                  {cities.length ? (
                    <ul className="cmpSuggest__list" role="listbox">
                      {cities.map((c) => (
                        <li key={c.key}>
                          <button type="button" className="cmpSuggest__item" onClick={() => addCity(c)}>
                            {c.name}
                            {c.region ? <span className="cmpSub">{c.region}</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="ecoCard cmpPanel">
              <h2 className="cmpPanel__title">Orçamento e período</h2>
              <EcoSegmented label="Orçamento" options={BUDGET_TYPES} value={budgetType} onChange={setBudgetType} disabled={locked} />
              <div className="cmpGrid cmpGrid--3">
                <label className="ecoField">
                  <span className="ecoLabel">{budgetType === "DIARIO" ? "Valor por dia (R$)" : "Valor total (R$)"}</span>
                  <input className="ecoInput" type="number" min={1} step={0.01} required value={budget} onChange={(e) => setBudget(e.target.value)} />
                </label>
                <label className="ecoField">
                  <span className="ecoLabel">Início</span>
                  <input className="ecoInput" type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={locked} />
                </label>
                <label className="ecoField">
                  <span className="ecoLabel">
                    Fim {budgetType === "DIARIO" ? <span className="ecoLabel__optional">(opcional)</span> : null}
                  </span>
                  <input
                    className="ecoInput"
                    type="date"
                    min={startDate}
                    required={budgetType === "TOTAL"}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </label>
              </div>
              <p className="cmpHint">
                {dailyEstimate != null
                  ? `Equivale a ${dailyEstimate.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} por dia. `
                  : ""}
                Teto de {settings ? settings.maxDailyBudget.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"} por dia. A cobrança é feita
                pela Meta, na forma de pagamento da conta de anúncios.
              </p>
            </section>
            </div>
            </div>

            <div className="cmpFooter">
              <button type="button" className="ecoBtn" onClick={goBack} disabled={busy}>
                Cancelar
              </button>
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy}>
                {busy ? "Salvando..." : id ? "Salvar" : "Criar rascunho"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};

export default CampanhaFormPage;
